<template>
  <div>
    <label class="font-weight-bold">Plantilla de la estampa</label>
    <p class="small text-muted mb-3">
      Configure cada línea de texto que aparecerá en su firma.
    </p>

    <!-- Líneas -->
    <div
      v-for="(line, index) in lines"
      :key="index"
      class="border rounded p-3 mb-3 bg-light"
    >
      <div class="d-flex justify-content-between align-items-center mb-2">
        <small class="font-weight-bold text-muted">
          {{ line.type === 'F' ? 'Firma manuscrita' : `Línea ${index + 1}` }}
        </small>
        <div>
          <b-button
            variant="link"
            size="sm"
            class="p-0 mr-2"
            @click="moveLine(index, -1)"
            :disabled="index === 0"
            title="Subir"
          >
            <feather-icon icon="ArrowUpIcon" size="14" />
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0 mr-2"
            @click="moveLine(index, 1)"
            :disabled="index === lines.length - 1"
            title="Bajar"
          >
            <feather-icon icon="ArrowDownIcon" size="14" />
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0 text-danger"
            @click="removeLine(index)"
            title="Eliminar"
          >
            <feather-icon icon="TrashIcon" size="14" />
          </b-button>
        </div>
      </div>

      <!-- Línea tipo Firma (imagen) -->
      <template v-if="line.type === 'F'">
        <div class="row mb-2">
          <div class="col-3">
            <label class="small">Pos. X</label>
            <input type="number" class="form-control form-control-sm" v-model.number="line.x" />
          </div>
          <div class="col-3">
            <label class="small">Pos. Y</label>
            <input type="number" class="form-control form-control-sm" v-model.number="line.y" />
          </div>
          <div class="col-2">
            <label class="small">Ancho</label>
            <input type="number" class="form-control form-control-sm" v-model.number="line.ax" min="0" title="0 = respeta el tamaño original" />
          </div>
          <div class="col-2">
            <label class="small">Alto</label>
            <input type="number" class="form-control form-control-sm" v-model.number="line.ay" min="0" title="0 = respeta el tamaño original" />
          </div>
          <div class="col-2 d-flex align-items-end flex-column justify-content-end">
            <b-button
              variant="outline-secondary"
              size="sm"
              block
              class="mb-1"
              @click="fitSignatureLine(index)"
              title="Ajustar el ancho y alto para que la firma quepa en la estampa"
            >
              Ajustar
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              block
              @click="centerSignatureLine(index)"
              title="Centrar en la estampa"
            >
              Centrar
            </b-button>
          </div>
        </div>
        <small class="text-muted">Se insertará la imagen de representación visual (firma manuscrita) configurada. Use 0 en Ancho o Alto para respetar el tamaño original de la imagen. "Ajustar" fija el ancho/alto al tamaño de la estampa; "Centrar" la posiciona al medio.</small>
      </template>

      <!-- Línea tipo Texto -->
      <template v-else>

      <div class="row mb-2">
        <div class="col-2">
          <label class="small">Tamaño</label>
          <select class="form-control form-control-sm" v-model="line.size">
            <option v-for="s in sizeOptions" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="col-2">
          <label class="small">Estilo</label>
          <select class="form-control form-control-sm" v-model="line.font">
            <option value="B">Negrita</option>
            <option value="P">Normal</option>
            <option value="I">Cursiva</option>
          </select>
        </div>
        <div class="col-2">
          <label class="small">Color</label>
          <select class="form-control form-control-sm" v-model="line.color">
            <option value="black">Negro</option>
            <option value="gray">Gris</option>
            <option value="blue">Azul</option>
            <option value="red">Rojo</option>
          </select>
        </div>
        <div class="col-3">
          <label class="small">Pos. X</label>
          <input
            type="number"
            class="form-control form-control-sm"
            v-model.number="line.x"
            min="0"
            max="200"
          />
        </div>
        <div class="col-3">
          <label class="small">Pos. Y</label>
          <input
            type="number"
            class="form-control form-control-sm"
            v-model.number="line.y"
            min="0"
            max="50"
          />
        </div>
      </div>

      <div>
        <label class="small">Texto</label>
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="line.text"
          :ref="'lineInput' + index"
          @focus="focusedLineIndex = index"
          placeholder="Escriba o inserte una variable..."
        />
      </div>
      </template>
    </div>

    <!-- Agregar línea -->
    <div class="d-flex mb-3">
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="addLine"
        class="mr-2"
      >
        <feather-icon icon="PlusIcon" size="14" class="mr-1" />
        Agregar línea de texto
      </b-button>
      <b-button
        variant="outline-info"
        size="sm"
        @click="addSignatureLine"
        :disabled="hasSignatureLine"
      >
        <feather-icon icon="ImageIcon" size="14" class="mr-1" />
        Agregar firma manuscrita
      </b-button>
    </div>

    <!-- Variables disponibles -->
    <div class="mb-3">
      <label class="small font-weight-bold text-muted">Variables disponibles:</label>
      <div class="d-flex flex-wrap gap-1 mt-1">
        <b-button
          v-for="variable in variables"
          :key="variable.value"
          variant="outline-info"
          size="sm"
          class="mr-1 mb-1"
          @click="insertVariable(variable.value)"
          :title="variable.description"
        >
          {{ variable.label }}
        </b-button>
      </div>
    </div>

    <!-- Modo avanzado (textarea) -->
    <div class="mt-3">
      <a href="#" class="small text-muted" @click.prevent="toggleAdvanced">
        {{ showAdvanced ? 'Ocultar modo avanzado' : 'Modo avanzado (editar código)' }}
      </a>
      <textarea
        v-if="showAdvanced"
        class="form-control mt-2"
        rows="4"
        :value="serialized"
        @input="onAdvancedInput($event.target.value)"
        style="font-size: 12px; font-family: monospace;"
      ></textarea>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StampTemplateEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    // Dimensiones lógicas de la estampa (mismas unidades que usa el backend).
    stampWidth: {
      type: [Number, String],
      default: 150
    },
    stampHeight: {
      type: [Number, String],
      default: 50
    }
  },
  data() {
    return {
      lines: [],
      showAdvanced: false,
      focusedLineIndex: 0,
      sizeOptions: [5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20],
      variables: [
        { label: 'Nombre', value: '${usuario.Usuario.nombre}', description: 'Nombre completo del usuario' },
        { label: 'Cargo', value: '${usuario.Usuario.cargo}', description: 'Cargo del usuario' },
        { label: 'RUT', value: '${usuario.Usuario.iut}', description: 'RUT del usuario' },
        { label: 'Email', value: '${usuario.Usuario.correo}', description: 'Correo del usuario' },
        { label: 'Empresa', value: '${empresa.Empresa.razonSocial}', description: 'Razón social de la empresa' },
        { label: 'Fecha', value: '${formatDate(fecha,"dd-MM-yyyy")}', description: 'Fecha actual' },
        { label: 'Fecha y hora', value: '${formatDate(fecha,"dd-MM-yyyy HH:mm")}', description: 'Fecha y hora actual' }
      ]
    };
  },
  computed: {
    serialized() {
      return this.lines.map(line => {
        if (line.type === 'F') {
          const fx = line.x !== undefined && line.x !== null ? line.x : 10;
          const fy = line.y !== undefined && line.y !== null ? line.y : 12;
          const fax = line.ax !== undefined && line.ax !== null ? line.ax : 100;
          const fay = line.ay !== undefined && line.ay !== null ? line.ay : 25;
          return `T=F;X=${fx};Y=${fy};AX=${fax};AY=${fay} |||| {firma}`;
        }
        const x = line.x !== undefined && line.x !== null ? line.x : 0;
        const y = line.y !== undefined && line.y !== null ? line.y : 15;
        return `S=${line.size};F=${line.font};C=${line.color};X=${x};Y=${y} |||| ${line.text}`;
      }).join('\n');
    },
    hasSignatureLine() {
      return this.lines.some(l => l.type === 'F');
    }
  },
  methods: {
    parse(template) {
      if (!template || !template.trim()) {
        this.lines = [this.createDefaultLine(15)];
        return;
      }
      const parts = template.split('\n').filter(p => p.trim());
      this.lines = parts.map(part => {
        const split = part.split('||||');
        if (split.length < 2) {
          return { type: 'T', size: 8, font: 'P', color: 'black', x: 0, y: 15, text: part.trim(), ax: 100, ay: 25 };
        }
        const config = split[0].trim();
        const text = split[1].trim();
        const line = { type: 'T', size: 8, font: 'P', color: 'black', x: 0, y: 15, text, ax: 100, ay: 25 };

        config.split(';').forEach(pair => {
          const [key, val] = pair.split('=');
          if (!key || !val) return;
          switch (key.trim()) {
            case 'S': line.size = parseInt(val) || 8; break;
            case 'F': line.font = val.trim(); break;
            case 'C': line.color = val.trim(); break;
            case 'X': line.x = parseInt(val) || 0; break;
            case 'Y': line.y = parseInt(val) || 15; break;
            case 'T': line.type = val.trim(); break;
            case 'AX': { const n = parseInt(val); line.ax = Number.isNaN(n) ? 100 : n; break; }
            case 'AY': { const n = parseInt(val); line.ay = Number.isNaN(n) ? 25 : n; break; }
          }
        });
        return line;
      });
      if (this.lines.length === 0) {
        this.lines = [this.createDefaultLine(15)];
      }
    },
    createDefaultLine(y) {
      return { type: 'T', size: 8, font: 'P', color: 'black', x: 0, y, text: '', ax: 100, ay: 25 };
    },
    addLine() {
      const lastY = this.lines.length > 0 ? this.lines[this.lines.length - 1].y : 7;
      this.lines.push(this.createDefaultLine(lastY + 8));
    },
    addSignatureLine() {
      this.lines.push({ type: 'F', x: 10, y: 12, ax: 100, ay: 25, size: 8, font: 'P', color: 'black', text: '{firma}' });
    },
    stampDimensions() {
      const w = parseInt(this.stampWidth, 10);
      const h = parseInt(this.stampHeight, 10);
      return {
        stampW: Number.isNaN(w) || w <= 0 ? 150 : w,
        stampH: Number.isNaN(h) || h <= 0 ? 50 : h
      };
    },
    centerSignatureLine(index) {
      const line = this.lines[index];
      if (!line || line.type !== 'F') return;
      const { stampW, stampH } = this.stampDimensions();
      line.x = Math.round((stampW - line.ax) / 2);
      line.y = Math.round((stampH - line.ay) / 2);
    },
    fitSignatureLine(index) {
      const line = this.lines[index];
      if (!line || line.type !== 'F') return;
      // Ajusta el ancho/alto para que la firma ocupe la estampa dejando un
      // pequeño margen, y luego la centra dentro del área.
      const { stampW, stampH } = this.stampDimensions();
      const margen = 4; // margen a cada lado (unidades lógicas)
      this.$set(line, 'ax', Math.max(1, stampW - margen * 2));
      this.$set(line, 'ay', Math.max(1, stampH - margen * 2));
      line.x = Math.round((stampW - line.ax) / 2);
      line.y = Math.round((stampH - line.ay) / 2);
    },
    removeLine(index) {
      if (this.lines.length <= 1) return;
      this.lines.splice(index, 1);
    },
    moveLine(index, direction) {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= this.lines.length) return;
      const temp = this.lines[index];
      this.$set(this.lines, index, this.lines[newIndex]);
      this.$set(this.lines, newIndex, temp);
    },
    insertVariable(variable) {
      const index = this.focusedLineIndex;
      if (index >= 0 && index < this.lines.length) {
        this.$set(this.lines[index], 'text', this.lines[index].text + variable);
      }
    },
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced;
    },
    onAdvancedInput(val) {
      this.parse(val);
    }
  },
  mounted() {
    this.parse(this.value);
  },
  watch: {
    value(newVal) {
      if (newVal !== this.serialized) {
        this.parse(newVal);
      }
    },
    lines: {
      handler() {
        this.$emit('input', this.serialized);
      },
      deep: true
    }
  },
  beforeUpdate() {
    // Track focused line
    for (let i = 0; i < this.lines.length; i++) {
      const ref = this.$refs['lineInput' + i];
      if (ref && ref[0] && ref[0] === document.activeElement) {
        this.focusedLineIndex = i;
        break;
      }
    }
  }
};
</script>
